import React, { useState, useCallback } from 'react'
import { Button, Card, CardHeader } from 'reactstrap'
import TitlePage from 'components/atoms/title-page'
import PropTypes from 'prop-types'
import ProductAttributePatternsApiV2 from 'api/v2/admins/product-attribute-patterns-api-v2'
import GenericModal from 'components/organisms/generic-modal'
import ProductPatternForm from 'domains/product-pattern/organisms/form'
import ProductAttributesApiV2 from 'api/v2/admins/product-attributes-api-v2'
import { ALERT_TYPES } from 'constants/alert-constants'
import ProductPatternTable from 'domains/product-pattern/organisms/table'

const propTypes = {
  pageUtils: PropTypes.shape({
    setAlertMsg    : PropTypes.func,
    setApiErrorMsg : PropTypes.func
  })
}

const defaultProps = {
  pageUtils: {
    setAlertMsg    : () => {},
    setApiErrorMsg : () => {}
  }
}

const ProductPatternListPage = ({ pageUtils }) => {
  const [ patterns, setPatterns ]                   = useState([])
  const [ selectedPattern, setSelectedPattern ]     = useState({})
  const [ isLoading, setIsLoading ]                 = useState(false)
  const [ isCreateModalOpen, setIsCreateModalOpen ] = useState(false)
  const [ isUpdateModalOpen, setIsUpdateModalOpen ] = useState(false)
  const [ tableState, setTableState ]               = useState({})
  const [ pagination, setPagination ]               = useState({})

  const handleFetchPatterns = useCallback(async (tableState) => {
    setIsLoading(true)
    await ProductAttributePatternsApiV2.get({ tableState })
      .then((response) => {
        setPatterns(response.data.patterns)
        setPagination(response.data.meta)
        setTableState(tableState)
      })
      .catch(() => {
        pageUtils.setAlertMsg('Unable to fetch Patterns', ALERT_TYPES.DANGER)
      })
      .finally(() => setIsLoading(false))
  }, [ pageUtils ])

  // func to create new product Pattern
  const handleCreatePattern = async (values, formikBag) => {
    formikBag.setSubmitting(true)
    await ProductAttributePatternsApiV2.create({ ...values })
      .then(() => {
        pageUtils.setAlertMsg('Product Pattern has been created', ALERT_TYPES.SUCCESS)
        toggleCreateModal()
        handleFetchPatterns(tableState)
        formikBag.resetForm()
      })
      .catch((error) => {
        pageUtils.setApiErrorMsg(error.response.data)
        formikBag.setErrors(error.response.data.messages)
      })
      .finally(() => formikBag.setSubmitting(false))
  }

  // func to update product Pattern
  const handleUpdatePattern = async (values, formikBag) => {
    formikBag.setSubmitting(true)
    await ProductAttributePatternsApiV2.update({ id: selectedPattern.id, ...values })
      .then(() => {
        pageUtils.setAlertMsg(`Product Pattern #${selectedPattern.id} has been updated`, ALERT_TYPES.SUCCESS)
        toggleUpdateModal()
        setSelectedPattern({})
        handleFetchPatterns(tableState)
      })
      .catch((error) => {
        pageUtils.setApiErrorMsg(error.response.data)
        formikBag.setErrors(error.response.data.messages)
      })
      .finally(() => formikBag.setSubmitting(false))
  }

  // func to change product Pattern status to `enabled`
  const handleEnablePattern = useCallback((datum) => {
    ProductAttributesApiV2.enableOption('pattern', datum.id)
      .then(() => {
        pageUtils.setAlertMsg(`Product Pattern #${datum.id} has been enabled`, ALERT_TYPES.SUCCESS)
        handleFetchPatterns(tableState)
      })
      .catch((error) => {
        pageUtils.setApiErrorMsg(error.response.data)
      })
  }, [ pageUtils, handleFetchPatterns, tableState ])

  // func to change product Pattern status to `disabled`
  const handleDisablePattern = useCallback((datum) => {
    ProductAttributesApiV2.disableOption('pattern', datum.id)
      .then(() => {
        pageUtils.setAlertMsg(`Product Pattern #${datum.label} has been disabled`, ALERT_TYPES.SUCCESS)
        handleFetchPatterns(tableState)
      })
      .catch((error) => {
        pageUtils.setApiErrorMsg(error.response.data)
      })
  }, [ pageUtils, handleFetchPatterns, tableState ])

  // func to open update modal form
  const handleOpenUpdateModal = useCallback((datum) => {
    setSelectedPattern(datum)
    setIsUpdateModalOpen(true)
  }, [ setIsUpdateModalOpen, setSelectedPattern ])

  const toggleCreateModal = () => setIsCreateModalOpen(!isCreateModalOpen)
  const toggleUpdateModal = () => setIsUpdateModalOpen(!isUpdateModalOpen)

  return (
    <>
      <TitlePage mainTitle='Product Patterns List'/>
      <Card>
        <CardHeader>
          <Button
            className='float-right'
            color='primary'
            onClick={ toggleCreateModal }
          >
            Add New Pattern
          </Button>
        </CardHeader>

        <ProductPatternTable
          data={ patterns }
          isLoading={ isLoading }
          pagination={ pagination }
          onFetchData={ handleFetchPatterns }
          rowButtonProps={{
            handleOpenUpdateModal : handleOpenUpdateModal,
            handleDisablePattern  : handleDisablePattern,
            handleEnablePattern   : handleEnablePattern
          }}
        />

      </Card>
      { /* Create Pattern Modal Form */ }
      <GenericModal
        isOpen={ isCreateModalOpen }
        toggle={ toggleCreateModal }
        title={ 'Create new Pattern' }
        size='md'
      >
        <ProductPatternForm
          handleSubmit={ handleCreatePattern }
          productPattern={{}}
        />
      </GenericModal>
      { /* product Pattern update Modal Form */ }
      <GenericModal
        isOpen={ isUpdateModalOpen }
        toggle={ toggleUpdateModal }
        title={ 'Update Pattern' }
        size='md'
      >
        <ProductPatternForm
          handleSubmit={ handleUpdatePattern }
          productPattern={ selectedPattern }
        />
      </GenericModal>
    </>
  )
}
ProductPatternListPage.propTypes    = propTypes
ProductPatternListPage.defaultProps = defaultProps

export default ProductPatternListPage